import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { TabView, TabPanel } from 'primereact/tabview';
import { errorService } from '../../services/ErrorService';
import { MedicalServiceService } from '../../service/MedicalServiceService';
import {
    sendWelcomeEmailAsync,
    sendPasswordEmailAsync,
    resetDevicesAsync,
    bulkSetSubscriptionStatusAsync,
    setGridState,
    selectGridState,
    setActiveIndex,
    selectActiveIndex,
    selectLoading,
    selectUserList,
    selectUserListCount,
    getUsersCountAsync,
    getTabCountAsync,
    getUsersAsync,
} from './userSlice';
import { showErrorToast, showSuccessToast } from '../../utils/ToastUtils';
import moment from 'moment';

const defaultGridParameters = { page: 0, first: 0, rows: 10, sortField: 'name', sortOrder: 1, filters: {} };

const medicalServiceService = new MedicalServiceService();

const subscriptionStatusOptions = [
    { label: 'Ativo', value: 'active' },
    { label: 'Inativo', value: 'inactive' },
];

// Grid de usuários reutilizada pelas duas abas (Alunos/Admins) — cada aba
// monta sua própria instância (com seu próprio estado de página/ordenação/
// busca), diferenciadas só pela prop isAdmin repassada pro backend. A ação
// de ativar/desativar assinatura em lote só faz sentido pra Alunos
// (enableBulk).
const UsersGrid = forwardRef(({ isAdmin, enableBulk, searchLabel, forceRefresh, incomingPreset, onPresetCleared }, ref) => {
    const dispatch = useDispatch();
    // Página/ordenação/busca preservados no Redux por aba (Alunos/Admins) —
    // sobrevive a essa grid desmontar (ex: navegar pra "Visualizar" e
    // voltar), em vez de sempre reiniciar do zero.
    const savedGridState = useSelector(selectGridState(isAdmin));

    const [displayWelcomeEmailDialog, setDisplayWelcomeEmailDialog] = useState(false);
    const [displayPasswordEmailDialog, setDisplayPasswordEmailDialog] = useState(false);
    const [displayResetDevicesDialog, setDisplayResetDevicesDialog] = useState(false);
    const [displayBulkStatusDialog, setDisplayBulkStatusDialog] = useState(false);
    // Qual ação o diálogo de Ativar/Desativar em lote está confirmando —
    // setado em askBulkStatus, lido em confirmBulkStatus quando o admin
    // clica "Sim".
    const [pendingBulkActive, setPendingBulkActive] = useState(false);
    // Motivo do cancelamento, só usado ao desativar — obrigatório aqui na UI
    // (bulkStatusConfirmDisabled), mas o backend ainda aceita vazio e cai no
    // default "Desativado pelo Admin" (chamadas fora dessa tela, ex. API).
    const [bulkStatusReason, setBulkStatusReason] = useState('');
    // Compartilhado pelos 4 diálogos de confirmação (Email boas-vindas/senha,
    // Resetar dispositivo, Ativar/Desativar em lote) — só um fica aberto por
    // vez, então um estado só já basta pra mostrar loading no botão "Sim"
    // enquanto confirma.
    const [confirming, setConfirming] = useState(false);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [gridParameters, setGridParameters] = useState(savedGridState.gridParameters);
    const [detail, setDetail] = useState({});
    const [searchInput, setSearchInput] = useState(savedGridState.search);
    const [search, setSearch] = useState(savedGridState.search);
    // Filtro vindo de um clique num card do dashboard ("Online agora",
    // "Risco de churn" etc.) — ver incomingPreset (Admins só nos presets de sessão).
    const [preset, setPreset] = useState(savedGridState.preset);
    const [presetLabel, setPresetLabel] = useState(savedGridState.presetLabel);
    // Filtros de coluna (Status só faz sentido em Alunos — enableBulk;
    // Serviço aparece nas duas abas, já que a coluna "Serviço" também).
    const [subscriptionStatus, setSubscriptionStatus] = useState(savedGridState.subscriptionStatus);
    const [medicalServiceId, setMedicalServiceId] = useState(savedGridState.medicalServiceId);
    const [medicalServiceOptions, setMedicalServiceOptions] = useState([]);
    // O userList/userListCount no Redux são compartilhados entre as duas
    // instâncias da grid (Alunos/Admins) — ao trocar de aba, a nova grid
    // monta e por um instante o Redux ainda tem os dados da aba anterior,
    // aparecendo registros errados até a nova busca responder. `ready`
    // mantém a tabela vazia (com o loading normal) até a primeira busca
    // desta instância (isAdmin correto) terminar.
    const [ready, setReady] = useState(false);
    // getUsersAsync e getUsersCountAsync são 2 thunks independentes que
    // dividem o mesmo status no Redux — a contagem costuma responder antes
    // da busca completa, e o status já vira 'idle' com os dados ainda
    // desatualizados (loading sumia antes da grid atualizar de verdade).
    // Loading local, só desliga quando as duas chamadas terminam.
    const [fetching, setFetching] = useState(false);

    const toast = useRef(null);
    const menu = useRef(null);
    const dt = useRef(null);

    useEffect(() => {
        medicalServiceService.getMedicalServices(0, 0, 500, 'name', 1)
            .then(res => setMedicalServiceOptions((res.data || []).map(m => ({ label: `${m.name} — ${m.state}`, value: m.id }))))
            .catch(() => setMedicalServiceOptions([]));
    }, []);

    const history = useHistory();
    const userList = useSelector(selectUserList(isAdmin));
    const userListCount = useSelector(selectUserListCount(isAdmin));
    const loading = fetching;

    const toggleMenu = (event, data) => {
        setDetail(data);
        menu.current.toggle(event);
    };

    const toolbarItems = [
        {
            label: 'Detalhes',
            icon: 'pi pi-eye',
            command: () => history.push(`/users/${detail.id}/details`, { user: detail })
        },
        {
            label: 'Atividades no app',
            icon: 'pi pi-history',
            command: () => history.push(`/users/${detail.id}/activity-timeline`, { user: detail })
        },
        {
            label: 'Email boas vindas',
            icon: 'pi pi-send',
            command: () => setDisplayWelcomeEmailDialog(true),
            disabled: detail.isVerifiedAccount
        },
        {
            label: 'Email senha',
            icon: 'pi pi-send',
            command: () => setDisplayPasswordEmailDialog(true)
        },
        {
            label: 'Resetar dispositivo',
            icon: 'pi pi-mobile',
            command: () => setDisplayResetDevicesDialog(true)
        },
    ];

    const fetchGrid = async (params, searchTerm, presetValue = preset, subscriptionStatusValue = subscriptionStatus, medicalServiceIdValue = medicalServiceId) => {
        setFetching(true);
        try {
            await errorService.retryOperation(async () => {
                await Promise.all([
                    dispatch(getUsersAsync({ ...params, isAdmin, search: searchTerm, preset: presetValue, subscriptionStatus: subscriptionStatusValue, medicalServiceId: medicalServiceIdValue })).unwrap(),
                    dispatch(getUsersCountAsync({ filters: params.filters, isAdmin, search: searchTerm, preset: presetValue, subscriptionStatus: subscriptionStatusValue, medicalServiceId: medicalServiceIdValue })).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao carregar usuários', error);
        } finally {
            setFetching(false);
        }
    };

    // Persiste no Redux pra sobreviver a essa grid desmontar/remontar.
    const persistGridState = (params, searchTerm, presetValue = preset, presetLabelValue = presetLabel, subscriptionStatusValue = subscriptionStatus, medicalServiceIdValue = medicalServiceId) => {
        dispatch(setGridState({ isAdmin, gridParameters: params, search: searchTerm, preset: presetValue, presetLabel: presetLabelValue, subscriptionStatus: subscriptionStatusValue, medicalServiceId: medicalServiceIdValue }));
    };

    useEffect(() => {
        // Preset veio de um clique num card do dashboard (a aba Admins só
        // recebe os presets de sessão — ver SESSION_PRESETS) — sempre tem
        // prioridade sobre o cache da aba, igual a uma navegação nova de verdade.
        if (incomingPreset) {
            setGridParameters(defaultGridParameters);
            setSearchInput('');
            setSearch('');
            setPreset(incomingPreset.preset);
            setPresetLabel(incomingPreset.presetLabel);
            setSubscriptionStatus(null);
            setMedicalServiceId(null);
            persistGridState(defaultGridParameters, '', incomingPreset.preset, incomingPreset.presetLabel, null, null);
            fetchGrid(defaultGridParameters, '', incomingPreset.preset, null, null).finally(() => setReady(true));
        // Se essa aba já carregou antes (ex: voltando de "Visualizar" com o
        // botão de voltar, ou trocando de aba e voltando), mostra os dados
        // guardados na hora, sem buscar de novo. Mas se chegou aqui por uma
        // navegação nova de verdade (ex: clicou no menu do sidebar, não veio
        // de "voltar"), volta pro padrão (primeira página, sem busca) e
        // busca de novo — `forceRefresh` é calculado uma vez só, no mount do
        // UsersView pai (ver history.action).
        } else if (savedGridState.loaded && !forceRefresh) {
            setReady(true);
        } else if (forceRefresh) {
            setGridParameters(defaultGridParameters);
            setSearchInput('');
            setSearch('');
            setPreset(null);
            setPresetLabel(null);
            setSubscriptionStatus(null);
            setMedicalServiceId(null);
            persistGridState(defaultGridParameters, '', null, null, null, null);
            fetchGrid(defaultGridParameters, '', null, null, null).finally(() => setReady(true));
        } else {
            fetchGrid(savedGridState.gridParameters, savedGridState.search, savedGridState.preset, savedGridState.subscriptionStatus, savedGridState.medicalServiceId).finally(() => setReady(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Limpa o filtro vindo do dashboard e volta a grid pro estado padrão.
    const clearPreset = () => {
        setPreset(null);
        setPresetLabel(null);
        const params = { ...gridParameters, page: 0, first: 0 };
        setGridParameters(params);
        persistGridState(params, search, null, null);
        fetchGrid(params, search, null);
        // Não reaplica o filtro do card ao voltar para esta aba.
        onPresetCleared?.(isAdmin);
    };

    const onSubscriptionStatusChange = (value) => {
        setSubscriptionStatus(value);
        const params = { ...gridParameters, page: 0, first: 0 };
        setGridParameters(params);
        persistGridState(params, search, preset, presetLabel, value, medicalServiceId);
        fetchGrid(params, search, preset, value, medicalServiceId);
    };

    const onMedicalServiceChange = (value) => {
        setMedicalServiceId(value);
        const params = { ...gridParameters, page: 0, first: 0 };
        setGridParameters(params);
        persistGridState(params, search, preset, presetLabel, subscriptionStatus, value);
        fetchGrid(params, search, preset, subscriptionStatus, value);
    };

    // Debounce: só busca 500ms depois que o usuário parar de digitar, em vez
    // de disparar uma requisição a cada tecla (mesmo padrão do ranking de
    // alunos em StudentsRankingTable.js).
    useEffect(() => {
        const handle = setTimeout(() => {
            if (searchInput !== search) {
                setSearch(searchInput);
                const params = { ...gridParameters, page: 0, first: 0 };
                setGridParameters(params);
                persistGridState(params, searchInput);
                fetchGrid(params, searchInput);
            }
        }, 500);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    // Botão ao lado do input — busca na hora, sem esperar os 500ms do debounce.
    const onSearchClick = () => {
        setSearch(searchInput);
        const params = { ...gridParameters, page: 0, first: 0 };
        setGridParameters(params);
        persistGridState(params, searchInput);
        fetchGrid(params, searchInput);
    };

    // Expõe um refresh() pro botão de recarregar que agora vive no
    // UsersView, acima do TabView — refaz a busca com os parâmetros atuais
    // (página/ordenação/busca), sem resetar nada.
    useImperativeHandle(ref, () => ({
        refresh: () => fetchGrid(gridParameters, search),
    }));

    const confirmWelcomeEmail = async () => {
        setConfirming(true);
        try {
            await errorService.retryOperation(async () => {
                await dispatch(sendWelcomeEmailAsync(detail.email)).unwrap();
                showSuccessToast(toast, 'Sucesso', 'Email enviado com sucesso');
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao enviar email de boas-vindas', error);
        }
        setConfirming(false);
        setDisplayWelcomeEmailDialog(false);
    };

    const cancelWelcomeEmail = () => {
        setDisplayWelcomeEmailDialog(false)
    };

    const welcomeEmailDialogFooter = (
        <>
            <Button type="button" label="Não" icon="pi pi-times" onClick={cancelWelcomeEmail} className="p-button-text" disabled={confirming} />
            <Button type="button" label="Sim" icon="pi pi-check" onClick={confirmWelcomeEmail} className="p-button-text" loading={confirming} autoFocus />
        </>
    );

    const confirmPasswordEmail = async () => {
        setConfirming(true);
        try {
            await errorService.retryOperation(async () => {
                await dispatch(sendPasswordEmailAsync(detail.email)).unwrap();
                showSuccessToast(toast, 'Sucesso', 'Email enviado com sucesso');
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao enviar email de senha', error);
        }
        setConfirming(false);
        setDisplayPasswordEmailDialog(false);
    };

    const cancelPasswordEmail = () => {
        setDisplayPasswordEmailDialog(false)
    };

    const passwordEmailDialogFooter = (
        <>
            <Button type="button" label="Não" icon="pi pi-times" onClick={cancelPasswordEmail} className="p-button-text" disabled={confirming} />
            <Button type="button" label="Sim" icon="pi pi-check" onClick={confirmPasswordEmail} className="p-button-text" loading={confirming} autoFocus />
        </>
    );

    // Desativa todos os dispositivos do aluno — não bloqueia acesso, só
    // limpa o "dispositivo ativo" atual; o próximo app aberto (dele mesmo
    // ou de outro dispositivo) se registra sozinho como o novo ativo.
    const confirmResetDevices = async () => {
        setConfirming(true);
        try {
            await errorService.retryOperation(async () => {
                await dispatch(resetDevicesAsync({ userId: detail.id, gridParameters, isAdmin, search, preset, subscriptionStatus, medicalServiceId })).unwrap();
                showSuccessToast(toast, 'Sucesso', 'Dispositivos resetados com sucesso');
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao resetar dispositivos', error);
        }
        setConfirming(false);
        setDisplayResetDevicesDialog(false);
    };

    const cancelResetDevices = () => {
        setDisplayResetDevicesDialog(false)
    };

    const resetDevicesDialogFooter = (
        <>
            <Button type="button" label="Não" icon="pi pi-times" onClick={cancelResetDevices} className="p-button-text" disabled={confirming} />
            <Button type="button" label="Sim" icon="pi pi-check" onClick={confirmResetDevices} className="p-button-text" loading={confirming} autoFocus />
        </>
    );

    const dateBodyTemplate = (rowData) => {
        const date = rowData.createdAt || rowData.date;
        if (!date) return null;
        return (
            <>
                <span className="p-column-title">Data</span>
                {moment(date).locale('pt-br').format('DD/MM/YYYY HH:mm')}
            </>
        );
    }

    // Mesmo padrão de badge do exemplo em UI Components > Table (product-badge
    // status-instock/outofstock) — verde/vermelho pra ativo/inativo.
    const activeStatusBodyTemplate = (rowData) => {
        const active = rowData.activeStatus === 'ativo';
        return <span className={`product-badge status-${active ? 'instock' : 'outofstock'}`}>{active ? 'Ativo' : 'Inativo'}</span>;
    }

    const actionBodyTemplate = (rowData) => {
        return <>
            <Menu ref={menu} model={toolbarItems} popup />
            <Button type="button" label="Ações" icon="pi pi-angle-down" onClick={(event) => toggleMenu(event, rowData)} style={{ width: 'auto' }} />
        </>
    }

    const onSort = async (event) => {
        setFetching(true);
        try {
            await errorService.retryOperation(async () => {
                const sortParams = {
                    ...gridParameters,
                    sortField: event.sortField,
                    sortOrder: event.sortOrder,
                };
                setGridParameters(sortParams);
                persistGridState(sortParams, search);
                await Promise.all([
                    dispatch(getUsersAsync({ ...sortParams, isAdmin, search, preset, subscriptionStatus, medicalServiceId })).unwrap(),
                    dispatch(getUsersCountAsync({ filters: sortParams.filters, isAdmin, search, preset, subscriptionStatus, medicalServiceId })).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao ordenar usuários', error);
        } finally {
            setFetching(false);
        }
    };

    const onPage = async (event) => {
        setFetching(true);
        try {
            await errorService.retryOperation(async () => {
                const pageParams = {
                    ...gridParameters,
                    page: event.page,
                    first: event.first,
                    rows: event.rows,
                };
                setGridParameters(pageParams);
                persistGridState(pageParams, search);
                await dispatch(getUsersAsync({ ...pageParams, isAdmin, search, preset, subscriptionStatus, medicalServiceId })).unwrap();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao mudar página', error);
        } finally {
            setFetching(false);
        }
    };

    const toggleSelectionMode = () => {
        setSelectionMode(prev => !prev);
        setSelectedUsers([]);
    };

    // Abre a confirmação em vez de executar direto — ativar/desativar em
    // lote afeta várias assinaturas de uma vez, então merece o mesmo
    // "tem certeza?" já usado pras outras ações (email, resetar dispositivo).
    const askBulkStatus = (active) => {
        setPendingBulkActive(active);
        setBulkStatusReason('');
        setDisplayBulkStatusDialog(true);
    };

    const confirmBulkStatus = async () => {
        const active = pendingBulkActive;
        setConfirming(true);
        try {
            await errorService.retryOperation(async () => {
                const userIds = selectedUsers.map(u => u.id);
                const { result } = await dispatch(bulkSetSubscriptionStatusAsync({ userIds, active, reason: bulkStatusReason, gridParameters, isAdmin, search, preset, subscriptionStatus, medicalServiceId })).unwrap();
                const message = active
                    ? `${result.updated} aluno(s) reativado(s)${result.skipped.length > 0 ? `, ${result.skipped.length} sem assinatura válida pra reativar` : ''}.`
                    : `${result.updated} aluno(s) desativado(s).`;
                showSuccessToast(toast, 'Sucesso', message);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao atualizar assinaturas em lote', error);
        } finally {
            setConfirming(false);
        }
        setDisplayBulkStatusDialog(false);
        setSelectionMode(false);
        setSelectedUsers([]);
    };

    const cancelBulkStatus = () => {
        setDisplayBulkStatusDialog(false);
    };

    // Motivo obrigatório só na UI (backend aceita vazio e cai no default
    // "Desativado pelo Admin") — força o admin a preencher antes de confirmar.
    const bulkStatusConfirmDisabled = !pendingBulkActive && !bulkStatusReason.trim();

    const bulkStatusDialogFooter = (
        <>
            <Button type="button" label="Não" icon="pi pi-times" onClick={cancelBulkStatus} className="p-button-text" disabled={confirming} />
            <Button type="button" label="Sim" icon="pi pi-check" onClick={confirmBulkStatus} className="p-button-text" loading={confirming} disabled={bulkStatusConfirmDisabled} autoFocus />
        </>
    );

    const exportCSV = () => {
        dt.current.exportCSV();
    }

    const leftToolbarTemplate = () => (
        <div className="flex gap-2">
            {!selectionMode ? (
                <Button label="Selecionar alunos" icon="pi pi-check-square" className="p-button-outlined" onClick={toggleSelectionMode} disabled={loading} />
            ) : (
                <>
                    <Button label={`Ativar (${selectedUsers.length})`} icon="pi pi-check" className="p-button-success" disabled={selectedUsers.length === 0} onClick={() => askBulkStatus(true)} />
                    <Button label={`Desativar (${selectedUsers.length})`} icon="pi pi-times" className="p-button-danger" disabled={selectedUsers.length === 0} onClick={() => askBulkStatus(false)} />
                    <Button label="Cancelar" className="p-button-text" onClick={toggleSelectionMode} />
                </>
            )}
        </div>
    );

    // Mesma disposição da tela PAGES > Crud (título/ações à esquerda, busca à
    // direita, tudo no header da própria DataTable) — mas mantendo o botão de
    // busca existente (não é globalFilter instantâneo como no Crud).
    const gridHeaderTemplate = () => (
        <div className="flex flex-column gap-3">
            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-end gap-3">
                <div className="flex flex-wrap align-items-end gap-3">
                    {enableBulk && (
                        <div className="flex flex-column gap-1">
                            <label htmlFor={`user-status-${isAdmin}`} className="text-xs text-color-secondary">Status</label>
                            <Dropdown
                                inputId={`user-status-${isAdmin}`}
                                value={subscriptionStatus}
                                options={subscriptionStatusOptions}
                                onChange={(e) => onSubscriptionStatusChange(e.value)}
                                placeholder="Todos"
                                showClear
                                disabled={loading}
                                className="w-full md:w-15rem"
                            />
                        </div>
                    )}
                    {enableBulk && (
                        <div className="flex flex-column gap-1">
                            <label htmlFor={`user-service-${isAdmin}`} className="text-xs text-color-secondary">Serviço</label>
                            <Dropdown
                                inputId={`user-service-${isAdmin}`}
                                value={medicalServiceId}
                                options={medicalServiceOptions}
                                onChange={(e) => onMedicalServiceChange(e.value)}
                                placeholder="Todos os serviços"
                                showClear
                                filter
                                disabled={loading}
                                className="w-full md:w-15rem"
                            />
                        </div>
                    )}
                </div>
                <div className="flex flex-column gap-1" style={{ width: '20rem' }}>
                    <label htmlFor={`user-search-${isAdmin}`} className="text-xs text-color-secondary">{searchLabel}</label>
                    <div className="flex gap-2">
                        <InputText
                            id={`user-search-${isAdmin}`}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Nome, email ou celular"
                            className="w-full"
                            disabled={loading}
                        />
                        {/* Mesmo padrão do exemplo Loading em UI Components > Button — o
                            próprio botão mostra o spinner (troca o ícone) enquanto a
                            grid está buscando, em vez de um spinner separado. */}
                        <Button icon="pi pi-search" loading={loading} onClick={onSearchClick} />
                    </div>
                </div>
            </div>
            {enableBulk && leftToolbarTemplate()}
        </div>
    );

    const baseColumns = [
        { field: 'name', header: 'Nome', sortable: true, style: { minWidth: '12rem' } },
        { field: 'email', header: 'Email', sortable: true, style: { minWidth: '12rem' } },
        { field: 'phone', header: 'Celular', style: { minWidth: '10rem' } },
        { field: 'service', header: 'Serviço', style: { minWidth: '10rem' } },
        ...(enableBulk ? [{ field: 'activeStatus', header: 'Status', body: activeStatusBodyTemplate, bodyClassName: 'text-center', style: { minWidth: '8rem' } }] : []),
        { field: 'createdAt', header: 'Data Cadastro', sortable: true, body: dateBodyTemplate, style: { minWidth: '10rem' } },
        { field: 'residence', header: 'Residências', style: { minWidth: '10rem' } },
    ];

    // Efeito de carregamento em Skeleton (UI Components > Misc) em vez do
    // spinner padrão do DataTable: enquanto busca, mostra linhas "vazias"
    // com um Skeleton no lugar de cada célula, no formato final da tabela.
    const showSkeleton = loading || !ready;
    const skeletonRows = Array.from({ length: gridParameters.rows }, (_, i) => ({ id: `skeleton-${i}` }));
    const displayValue = showSkeleton ? skeletonRows : userList;
    const displayColumns = showSkeleton
        ? baseColumns.map(col => ({ ...col, body: () => <Skeleton />, sortable: false }))
        : baseColumns;

    return (
        <>
            <Toast ref={toast} />

            {presetLabel && (
                <div className="mb-3">
                    <Chip label={`Filtro: ${presetLabel}`} icon="pi pi-filter" removable onRemove={clearPreset} />
                </div>
            )}

            <DataTable ref={dt} value={displayValue}
                header={gridHeaderTemplate()}
                selection={enableBulk ? selectedUsers : null}
                onSelectionChange={enableBulk ? (e) => setSelectedUsers(e.value) : undefined}
                dataKey="id"
                paginator
                sortField={gridParameters.sortField}
                sortOrder={gridParameters.sortOrder}
                rows={gridParameters.rows}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                className="datatable-responsive"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Exibindo de {first} à {last} de {totalRecords} registros"
                emptyMessage="Sem resultados."
                responsiveLayout="scroll"
                totalRecords={ready ? userListCount : 0}
                lazy={true}
                first={gridParameters.first}
                onSort={(e) => onSort(e)}
                onPage={(e) => onPage(e)}
                >
                {enableBulk && selectionMode && <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />}
                <Column field="id" hidden></Column>
                <Column field="isVerifiedAccount" hidden></Column>
                {displayColumns.map(col => <Column key={col.field} {...col} />)}
                <Column body={showSkeleton ? () => <Skeleton /> : actionBodyTemplate} style={{ width: '15%', minWidth: '10rem' }}></Column>
            </DataTable>

            <Dialog header="Enviar email de boas-vindas" visible={displayWelcomeEmailDialog} onHide={() => setDisplayWelcomeEmailDialog(false)} style={{ width: '400px' }} modal footer={welcomeEmailDialogFooter}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Deseja enviar o email de boas-vindas para {detail.email}?</span>
                </div>
            </Dialog>
            <Dialog header="Enviar email de redefinição de senha" visible={displayPasswordEmailDialog} onHide={() => setDisplayPasswordEmailDialog(false)} style={{ width: '400px' }} modal footer={passwordEmailDialogFooter}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Deseja enviar o email de redefinição de senha para {detail.email}?</span>
                </div>
            </Dialog>
            <Dialog header="Resetar dispositivo" visible={displayResetDevicesDialog} onHide={() => setDisplayResetDevicesDialog(false)} style={{ width: '400px' }} modal footer={resetDevicesDialogFooter}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>Isso não bloqueia o acesso do aluno — só limpa o dispositivo ativo atual. Na próxima vez que abrir o app, o dispositivo em uso se torna o novo ativo automaticamente. Deseja continuar?</span>
                </div>
            </Dialog>
            <Dialog header={pendingBulkActive ? 'Ativar assinaturas' : 'Desativar assinaturas'} visible={displayBulkStatusDialog} onHide={cancelBulkStatus} style={{ width: '400px' }} modal footer={bulkStatusDialogFooter}>
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>
                        {pendingBulkActive
                            ? `Deseja reativar a assinatura de ${selectedUsers.length} aluno(s) selecionado(s)?`
                            : `Deseja desativar a assinatura de ${selectedUsers.length} aluno(s) selecionado(s)?`}
                    </span>
                </div>
                {!pendingBulkActive && (
                    <div className="flex flex-column gap-1 mt-3">
                        <label htmlFor="bulk-status-reason" className="text-xs text-color-secondary">Motivo *</label>
                        <InputText
                            id="bulk-status-reason"
                            value={bulkStatusReason}
                            onChange={(e) => setBulkStatusReason(e.target.value)}
                            placeholder="Descreva o motivo da desativação"
                            disabled={confirming}
                            className="w-full"
                        />
                    </div>
                )}
            </Dialog>
        </>
    );
});

// Presets de sessão no app (cards "Online agora" e "Usuários com sessão
// hoje") — valem para alunos e admins, cada um na sua aba.
const SESSION_PRESETS = ['onlineNow', 'sessionToday'];

const UsersView = ({ location }) => {
    const dispatch = useDispatch();
    // Guardado no Redux (não em useState local) — sobrevive ao UsersView
    // desmontar/remontar (ex: voltar de "Visualizar"), senão sempre voltava
    // pra aba Alunos independente de onde o admin estava antes.
    const activeIndex = useSelector(selectActiveIndex);
    const gridRef = useRef(null);
    const loading = useSelector(selectLoading);
    const history = useHistory();
    // Calculado uma vez só, no primeiro mount: chegou aqui por um "voltar"
    // (POP — botão de voltar da tela de detalhes) ou por uma navegação nova
    // de verdade (PUSH — ex: clicou no menu do sidebar)? Só no segundo caso
    // força uma busca real ignorando o cache das abas.
    const forceRefreshRef = useRef(history.action !== 'POP');
    // Filtro vindo de um clique num card do dashboard (ver StatCard onClick
    // em ActiveUsersCard.js e afins). A aba Alunos sempre recebe. A aba
    // Admins só recebe os presets de sessão (SESSION_PRESETS): esses cards
    // contam qualquer usuário com sessão no app, admins inclusive — os
    // demais (assinatura, churn...) são só sobre alunos.
    const incomingPresetRef = useRef(
        location?.state?.preset ? { preset: location.state.preset, presetLabel: location.state.presetLabel } : null
    );
    const adminIncomingPreset = SESSION_PRESETS.includes(incomingPresetRef.current?.preset)
        ? incomingPresetRef.current
        : null;
    const alunosState = useSelector(selectGridState(false));
    const adminsState = useSelector(selectGridState(true));
    // Abas em que o filtro do card já foi removido (o "x" do chip). O
    // TabView remonta a aba ao alternar, e sem isso ela reaplicaria o
    // filtro recebido do card. Cada aba limpa só o próprio filtro.
    const [presetClearedTabs, setPresetClearedTabs] = useState({ alunos: false, admins: false });
    const alunosIncomingPreset = presetClearedTabs.alunos ? null : incomingPresetRef.current;
    const adminsIncomingPreset = presetClearedTabs.admins ? null : adminIncomingPreset;

    const handlePresetCleared = (isAdmin) => {
        setPresetClearedTabs(prev => ({ ...prev, [isAdmin ? 'admins' : 'alunos']: true }));
    };

    // Navegação nova de verdade (não veio de "voltar") sempre reseta pra
    // aba Alunos, mesmo que o admin estivesse em Admins antes.
    // Total da aba que não abre agora (o TabView só monta a aba ativa) —
    // com os mesmos filtros que ela terá ao ser aberta, para o número do
    // cabeçalho já bater. A aba visível atualiza o próprio total ao buscar.
    const fetchInactiveTabCount = () => {
        const initialIndex = forceRefreshRef.current ? 0 : activeIndex;
        const isAdmin = initialIndex === 0;
        const tabState = isAdmin ? adminsState : alunosState;
        const tabPreset = isAdmin ? adminIncomingPreset : incomingPresetRef.current;
        if (forceRefreshRef.current || tabPreset) {
            dispatch(getTabCountAsync({ isAdmin, preset: tabPreset?.preset ?? null }));
        } else if (!tabState.countLoaded) {
            dispatch(getTabCountAsync({
                isAdmin,
                filters: tabState.gridParameters.filters,
                search: tabState.search,
                preset: tabState.preset,
                subscriptionStatus: tabState.subscriptionStatus,
                medicalServiceId: tabState.medicalServiceId,
            }));
        }
    };

    useEffect(() => {
        fetchInactiveTabCount();
        if (forceRefreshRef.current) {
            dispatch(setActiveIndex(0));
        }
        if (incomingPresetRef.current) {
            // Limpa o state da entrada do histórico — sem isso, voltar/avançar
            // pra essa mesma entrada mais tarde reaplicaria o filtro do zero.
            history.replace(location.pathname);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const title = activeIndex === 0 ? 'Gestão de usuários — Alunos' : 'Gestão de usuários — Admins';

    // Total geral da aba com todos os filtros dela (busca, card do dashboard,
    // status, serviço) — o mesmo /users/count da paginação, não a página atual.
    const tabHeader = (label, tabState) =>
        tabState.countLoaded ? `${label} (${tabState.userListCount.toLocaleString('pt-BR')})` : label;

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <div className="flex justify-content-between align-items-center mb-3">
                        <h5 className="m-0">{title}</h5>
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            onClick={() => gridRef.current?.refresh()}
                            disabled={loading}
                        />
                    </div>
                    <TabView activeIndex={activeIndex} onTabChange={(e) => dispatch(setActiveIndex(e.index))}>
                        <TabPanel header={tabHeader('Alunos', alunosState)}>
                            <UsersGrid ref={gridRef} isAdmin={false} enableBulk={true} searchLabel="Buscar aluno" forceRefresh={forceRefreshRef.current} incomingPreset={alunosIncomingPreset} onPresetCleared={handlePresetCleared} />
                        </TabPanel>
                        <TabPanel header={tabHeader('Admins', adminsState)}>
                            <UsersGrid ref={gridRef} isAdmin={true} enableBulk={false} searchLabel="Buscar admin" forceRefresh={forceRefreshRef.current} incomingPreset={adminsIncomingPreset} onPresetCleared={handlePresetCleared} />
                        </TabPanel>
                    </TabView>
                </div>
            </div>
        </div>
    );
}

const comparisonFn = function (prevProps, nextProps) {
    return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(UsersView, comparisonFn);
